import "./AddUpdateResupplySettings.css";
import { Box, Grid2, TextField } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import {
  checkCanUpdate,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import {
  addResupplyManagementService,
  fetchDrugTypeService,
  fetchResupplySettingsByIdService,
  updateResupplyManagementService,
} from "../../../services/resupplySettingsService";
import ResupplySwitch from "../../../components/CustomSwitch/ResupplySwitch";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";

const AddUpdateResupplySettings = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);
  const [drugList, setDrugList] = useState([]);
  const [unchangedDrugList, setUnchangedDrugList] = useState([]);
  const [canUpdateResupplySetting, setCanUpdateResupplySetting] =
    useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const navigate = useNavigate();

  const access_token = localStorage.getItem("access_token");
  const role = localStorage.getItem("role");

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  function getDrugValidation() {
    return yup.object({
      minBuffer: yup
        .number()
        .typeError("Must be a number")
        .required("Required"),
      maxBuffer: yup
        .number()
        .typeError("Must be a number")
        .required("Required"),
      shortWindow: yup
        .number()
        .typeError("Must be a number")
        .required("Required"),
      longWindow: yup
        .number()
        .typeError("Must be a number")
        .required("Required"),
      initialQuantity: yup
        .number()
        .typeError("Must be a number")
        .required("Required"),
      predictForDrugType: yup.boolean(),
    });
  }

  function getDefaultValues(drugList) {
    const settings = {};

    drugList.forEach((drug) => {
      settings[drug.drugName] = {
        minBuffer: "",
        maxBuffer: "",
        shortWindow: "",
        longWindow: "",
        initialQuantity: "",
        predictForDrugType: false,
      };
    });

    return {
      name: "",
      settings,
    };
  }

  function createRoleSchema(drugList) {
    const settingsShape = {};

    drugList.forEach((drug) => {
      settingsShape[drug.drugName] = getDrugValidation();
    });

    return yup.object().shape({
      name: yup.string().required("Name is required"),
      settings: yup.object().shape(settingsShape),
    });
  }

  const form = useForm({
    resolver: yupResolver(createRoleSchema(drugList)),
    defaultValues: getDefaultValues(drugList),
  });

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = form;

  useNavigationGuard(isDirty);

  useEffect(() => {
    getDrugListData();
  }, []);

  useEffect(() => {
    if (id && unchangedDrugList && unchangedDrugList?.length > 0) {
      getResupplySettingsById(id);
    }
  }, [id, unchangedDrugList]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanUpdateResupplySettings"
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateResupplySetting(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateResupplySetting(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (canUpdateResupplySetting) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateResupplySetting]);

  const getResupplySettingsById = async (id) => {
    setLoad(true);
    try {
      const res = await fetchResupplySettingsByIdService(access_token, id);
      if (res?.status == 200) {
        const resp = res?.data?.result;
        setLoad(false);
        if (resp?.resupplyTypeDrugTypeDTO?.length > 0) {
          reset({
            name: resp.name,
            settings: resp.resupplyTypeDrugTypeDTO?.reduce((acc, drug) => {
              acc[drug.drugName] = {
                minBuffer: drug.minBuffer,
                maxBuffer: drug.maxBuffer,
                shortWindow: drug.shortWindow,
                longWindow: drug.longWindow,
                initialQuantity: drug.quantityForInitialOrder,
                predictForDrugType: drug.predictForDrugType,
              };
              return acc;
            }, {}),
          });
          setDrugList(resp.resupplyTypeDrugTypeDTO);
        } else {
          reset({
            name: resp.name,
            settings:
              unchangedDrugList &&
              unchangedDrugList.reduce((acc, drug) => {
                acc[drug.drugName] = {
                  minBuffer: drug.minBuffer,
                  maxBuffer: drug.maxBuffer,
                  shortWindow: drug.shortWindow,
                  longWindow: drug.longWindow,
                  initialQuantity: drug.quantityForInitialOrder,
                  predictForDrugType: drug.predictForDrugType,
                };
                return acc;
              }, {}),
          });
          setDrugList(unchangedDrugList);
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const getDrugListData = async () => {
    try {
      setLoad(true);
      const res = await fetchDrugTypeService(access_token);
      if (res?.status == 200) {
        setLoad(false);
        const resp = res?.data?.result;
        setUnchangedDrugList(resp);
        if (!id) {
          reset({
            // name: "",
            settings: resp.reduce((acc, drug) => {
              acc[drug.drugName] = {
                minBuffer: drug.minBuffer,
                maxBuffer: drug.maxBuffer,
                shortWindow: drug.shortWindow,
                longWindow: drug.longWindow,
                initialQuantity: drug.quantityForInitialOrder,
                predictForDrugType: drug.predictForDrugType,
              };
              return acc;
            }, {}),
          });
          setDrugList(resp);
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const addUpdateRespplySettings = async (data) => {
    const updatedList = drugList.map((drug) => {
      const formValues = data.settings[drug.drugName];

      return {
        resupplyTypeId: drug.resupplyTypeId,
        drugTypeId: drug.drugTypeId,
        drugName: drug.drugName,
        minBuffer: formValues.minBuffer,
        maxBuffer: formValues.maxBuffer,
        shortWindow: formValues.shortWindow,
        longWindow: formValues.longWindow,
        quantityForInitialOrder: formValues.initialQuantity,
        predictForDrugType: formValues.predictForDrugType,
      };
    });
    setLoad(true);
    const newData = {
      name: data.name,
      resupplyTypeDrugTypeList: updatedList,
    };
    try {
      if (!id) {
        const res = await addResupplyManagementService(access_token, newData);
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Resupply Setting is created successfully !");
          reset();
          navigate("/resupply-settings");
        }
      } else {
        const res = await updateResupplyManagementService(
          access_token,
          newData,
          id
        );
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Resupply Setting is Updated successfully !");
          reset();
          navigate("/resupply-settings");
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(
                  e,
                  isDirty,
                  "resupply-settings",
                  navigate
                )
              }
            >
              Resupply Setting
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              to="/addUpdate-resupply-settings"
            >
              {id ? "Edit Resupply Setting" : "Add Resupply Setting"}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Resupply Setting Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addUpdateRespplySettings)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 6, md: 6, lg: 6, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Name
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    disabled={isReadOnly}
                    fullWidth
                    type={"text"}
                    {...register("name")}
                  />
                </div>
                {errors.name && (
                  <div className="error-container">{errors.name.message}</div>
                )}
              </Grid2>
              <div className="responsive-table-wrapper">
                <table className="resupply-table resupply-table-header">
                  <thead>
                    <tr>
                      <th>Drug Name</th>
                      <th>Min Buffer</th>
                      <th>Max Buffer</th>
                      <th>Short Window</th>
                      <th>Long Window</th>
                      <th>Initial Quantity</th>
                      <th>Predict for this Drug Type</th>
                    </tr>
                  </thead>
                </table>
                <div className="resupply-table-body-wrapper">
                  <table className="resupply-table">
                    <tbody>
                      {drugList &&
                        drugList.map((drug, i) => (
                          <tr key={i}>
                            <td>
                              <strong>{drug.drugName}</strong>
                            </td>

                            {[
                              "minBuffer",
                              "maxBuffer",
                              "shortWindow",
                              "longWindow",
                              "initialQuantity",
                            ].map((field) => (
                              <td key={field}>
                                <div className="input-wrapper">
                                  <input
                                    type="number"
                                    disabled={isReadOnly}
                                    {...register(
                                      `settings.${drug.drugName}.${field}`
                                    )}
                                    className="resupply-input"
                                  />
                                  {errors?.settings?.[drug.drugName]?.[
                                    field
                                  ] && (
                                    <p className="error-text">
                                      {
                                        errors.settings[drug.drugName][field]
                                          .message ||
                                          "\u00A0" /* NBSP to reserve space */
                                      }
                                    </p>
                                  )}
                                </div>
                              </td>
                            ))}

                            <td>
                              <ResupplySwitch
                                value={watch(
                                  `settings.${drug.drugName}.predictForDrugType`
                                )}
                                disabled={isReadOnly}
                                onChange={(val) =>
                                  setValue(
                                    `settings.${drug.drugName}.predictForDrugType`,
                                    val
                                  )
                                }
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "resupply-settings",
                      navigate
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                {checkCanUpdate(id, isReadOnly, role) && (
                  <button
                    type="submit"
                    disabled={isReadOnly}
                    className={isReadOnly ? "disabled-btn" : "create-btn"}
                  >
                    {id ? "Update" : "Save"}
                  </button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default AddUpdateResupplySettings;
